import { ChevronLeftIcon } from '@heroicons/react/24/outline';

/**
 * "Previous" — step back to the question already dealt with (client request, 2026-08-03).
 *
 * It lives here rather than on either page because BOTH table sessions carry it and the
 * learner meets it through a dozen different doors: every Bidding Practice mode on
 * `/problems` (level practice, Important Sequences, Review, Overcalling, 3rd-level bid) and
 * every module on `/module-practice/…`. One component is what stops the two from drifting into
 * two slightly different controls for one action.
 *
 * Stepping back is READ-ONLY on both surfaces: the earlier question comes back with the answer
 * that was given and the verdict it earned, and nothing is re-graded. That is what keeps the
 * score and the streak honest — walking back and forth cannot move either.
 *
 * It shares the secondary brass-on-cream shape of the Skip button it sits beside, because they
 * are the same weight of action: neither is the way forward. The label drops on the shortest
 * landscape phones and below 360px, where the decision column is a third of the row and the
 * chevron alone has to carry it — the accessible name stays on the button either way, the same
 * trade "End Session" makes in the masthead.
 *
 * SIZING: its own `short:` step exists for the verdict row, where it stands beside a "Next
 * Problem" that compacts the same way — matching it there is what keeps that row's height off the
 * shortest landscape phones' budget. It does NOT match the Skip/Next buttons on the pad rows,
 * which carry no `short:` step, so on any phone under 750px tall it rendered ~28px against their
 * 40px and read as a half-sized afterthought (client report, 2026-08-03). Those rows therefore
 * pass `items-stretch` and let this button take the row's height, rather than this component
 * hard-coding a height that would be wrong in the other row. Note the rows must NOT also set a
 * height on it (`h-full` and friends): an explicit cross size is precisely what switches
 * `align-items: stretch` off, and `height: 100%` against an auto-height row then resolves back to
 * the content height — measured 30px beside a 44.5px Skip, i.e. the bug it was meant to fix.
 */
export function PreviousButton({
  onClick,
  disabled,
  className,
  testId = 'previous-question',
}: {
  onClick: () => void;
  disabled?: boolean;
  /** Extra layout classes from the row this sits in (widths, wrapping). */
  className?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label="Previous"
      title="Previous"
      data-testid={testId}
      className={
        'inline-flex items-center justify-center gap-1 rounded-lg border border-brass ' +
        'bg-cream px-4 py-2.5 text-[15px] font-medium text-ink short:px-3 short:py-1 short:text-sm ' +
        'hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-brass ' +
        'disabled:cursor-not-allowed disabled:opacity-50' +
        (className ? ` ${className}` : '')
      }
    >
      <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
      <span className="flat-ls:hidden xnarrow:hidden">Previous</span>
    </button>
  );
}
